import { useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { apiErr } from "@/lib/format";
import { ArrowLeftIcon, EnvelopeSimpleIcon, CheckCircleIcon } from "@phosphor-icons/react";

/**
 * /forgot-password
 * Formulario para solicitar link de restablecimiento.
 * Muestra siempre el mismo mensaje de éxito (no revela si el email existe).
 */
export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
      setSent(true);
    } catch (e2) {
      setErr(apiErr(e2));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-app items-center justify-center p-6">
      <div className="w-full max-w-md bg-card border border-border rounded-2xl p-8 shadow-xl" data-testid="forgot-password-card">
        <div className="flex items-center justify-center mb-6">
          <img src="/agroport-logo-green.png" alt="AGROPORT" className="h-14 w-auto" />
        </div>

        {sent ? (
          <div className="text-center space-y-4" data-testid="forgot-password-success">
            <CheckCircleIcon size={48} weight="fill" className="text-agro-green mx-auto" />
            <h2 className="font-heading text-2xl font-semibold text-text-primary">Revisa tu correo</h2>
            <p className="text-sm text-text-secondary leading-relaxed">
              Si el correo <b className="text-text-primary">{email}</b> está registrado, recibirás un enlace
              para restablecer tu contraseña en los próximos minutos.
              <br /><br />
              El enlace es válido por <b>30 minutos</b>.
            </p>
            <div className="pt-4">
              <Link
                to="/login"
                className="inline-flex items-center gap-2 text-sm text-agro-green hover:text-agro-green-hover transition"
                data-testid="back-to-login"
              >
                <ArrowLeftIcon size={14} /> Volver a iniciar sesión
              </Link>
            </div>
          </div>
        ) : (
          <>
            <div className="text-center mb-6">
              <div className="text-[10px] uppercase tracking-[0.3em] text-agro-green font-medium mb-2">Recuperar acceso</div>
              <h2 className="font-heading text-2xl font-semibold text-text-primary">¿Olvidaste tu contraseña?</h2>
              <p className="text-sm text-text-secondary mt-2">
                Ingresa tu correo y te enviaremos un enlace para crear una nueva contraseña.
              </p>
            </div>

            <form onSubmit={submit} className="space-y-4" data-testid="forgot-password-form">
              <div>
                <label className="text-xs uppercase tracking-wider text-text-secondary font-medium mb-2 block">Correo</label>
                <div className="relative">
                  <EnvelopeSimpleIcon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
                  <input
                    data-testid="forgot-email-input"
                    type="email"
                    autoComplete="username"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="w-full bg-app border border-border rounded-md pl-10 pr-3 py-3 text-sm text-text-primary focus:border-agro-green"
                    placeholder="usuario@agroport.hn"
                  />
                </div>
              </div>

              {err && (
                <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-md px-3 py-2" data-testid="forgot-error">
                  {err}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                data-testid="forgot-submit"
                className="w-full bg-agro-green hover:bg-agro-green-hover text-black font-semibold rounded-md py-3 transition-all active:scale-[0.98] disabled:opacity-50"
              >
                {loading ? "Enviando…" : "Enviar enlace de restablecimiento"}
              </button>

              <div className="text-center pt-2">
                <Link
                  to="/login"
                  className="inline-flex items-center gap-1.5 text-xs text-text-muted hover:text-agro-green transition"
                  data-testid="cancel-back-to-login"
                >
                  <ArrowLeftIcon size={12} /> Volver a iniciar sesión
                </Link>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
